import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithRef, ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden'
import s from './AppShell.module.css'

/**
 * Coquille d'application — la colonne pleine hauteur que les deux fronts posent à la
 * racine : une bande de navigation à gauche, le contenu à droite.
 *
 * ```tsx
 * <AppShell>
 *   <nav className={s.rail}>…</nav>
 *   <AppShellContent>
 *     <Outlet />
 *   </AppShellContent>
 * </AppShell>
 * ```
 *
 * Ce que l'app garde pour elle : la bande elle-même — rail de 88 px côté terrain,
 * bandeau de 248 px côté back-office — sa marque, ses groupes, son indicateur réseau.
 * Ce paquet ne connaît aucun routeur : les liens arrivent en `render` (voir `NavItem`).
 */
const shell = cva(s.shell, {
  variants: {
    /**
     * `viewport` borne la coquille à la hauteur de la fenêtre et lui interdit de
     * déborder : la bande de navigation tient toute la hauteur, et c'est `PageBody` qui
     * défile, pas le document. C'est la coquille du terrain (tablette, cadre fixe) ; le
     * back-office garde le défilement de page.
     */
    height: {
      auto: null,
      viewport: s.viewport,
    },
  },
  defaultVariants: { height: 'auto' },
})

export type AppShellProps = ComponentPropsWithRef<'div'> & VariantProps<typeof shell>

export function AppShell({ height, className, ...props }: AppShellProps) {
  return <div className={shell({ height, className })} {...props} />
}

/** Zone de contenu de la coquille — le `<main>` où le routeur rend l'écran courant. */
export function AppShellContent({ className, ...props }: ComponentPropsWithRef<'main'>) {
  return <main className={cn(s.content, className)} {...props} />
}

const navItem = cva(s.item, {
  variants: {
    orientation: {
      /** Icône à gauche du libellé — bandeau latéral. */
      horizontal: s.horizontal,
      /** Icône au-dessus du libellé — rail étroit. */
      vertical: s.vertical,
    },
  },
  defaultVariants: { orientation: 'horizontal' },
})

export interface NavItemProps
  extends Omit<useRender.ComponentProps<'a'>, 'children'>, VariantProps<typeof navItem> {
  /** L'icône, en slot : c'est l'app qui choisit le jeu d'icônes et sa taille. */
  icon?: ReactNode
  /** Libellé visible — il est aussi le nom accessible du lien. */
  label: ReactNode
  /**
   * Grisé et non navigable — un écran que le rôle courant ne peut pas ouvrir. `render` est
   * alors **ignoré** plutôt qu'habillé : intercepter le clic d'un `<Link>` dépendrait de
   * l'ordre dans lequel `useRender` compose les gestionnaires d'événements, qui n'est pas
   * garanti. Un `<span>` qui ne porte aucun `href` ne navigue par construction, jamais par
   * interception.
   */
  disabled?: boolean
  /**
   * La raison, en tooltip natif (`title`) — la seule explication que ce composant écrit
   * lui-même plutôt que de la recevoir en slot, parce qu'elle n'a de sens qu'accolée à
   * `disabled`. Pas de primitive dédiée : la plateforme suffit (cf. `packages/ui/CLAUDE.md`).
   */
  disabledReason?: string
  /**
   * Une pastille de compte — `.rail-badge` du prototype terrain. **Cachée à zéro**, comme au
   * prototype : un « 0 » ferait chercher un signal qui n'existe pas.
   *
   * Le chiffre est décoratif pour la synthèse vocale ; `label` le dit en mots (« 2 à traiter »)
   * et rejoint le nom du lien. Un chiffre seul se lirait « Notifs 2 », sans dire 2 quoi.
   */
  badge?: { count: number; label: string }
}

/**
 * Un lien de la navigation principale — icône + libellé, état de survol, état actif.
 *
 * **L'état actif n'est pas une prop** : le routeur pose `aria-current="page"` sur le lien
 * courant, et le module CSS s'appuie dessus. Une prop `active` ferait exister une seconde
 * source de vérité, forcément désynchronisable.
 *
 * La prop `render` (idiome Base UI, via `useRender`) habille l'élément de l'app —
 * typiquement le `Link` de son routeur :
 *
 * ```tsx
 * <NavItem
 *   icon={<CalendarDays size={18} aria-hidden />}
 *   label="Planning"
 *   render={<Link to="/planning" />}
 * />
 * ```
 */
export function NavItem({
  icon,
  label,
  orientation,
  disabled,
  disabledReason,
  badge,
  render,
  className,
  ...props
}: NavItemProps) {
  const counted = badge !== undefined && badge.count > 0

  return useRender({
    render: disabled ? <span /> : render,
    defaultTagName: 'a',
    props: {
      className: cn(navItem({ orientation }), className),
      ...props,
      ...(disabled ? { 'aria-disabled': true, tabIndex: 0, title: disabledReason } : null),
      children: (
        <>
          {icon}
          {label}
          {counted && (
            <>
              <span className={s.badge} aria-hidden>
                {badge.count}
              </span>
              <VisuallyHidden>, {badge.label}</VisuallyHidden>
            </>
          )}
        </>
      ),
    },
  })
}
